import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { useAllCached, type UseAllCachedOptions } from '../shared/use-all-cached';
import { requireWorkspaceId } from '../shared/workspace';
import { environmentPath, snippetPath } from './api-path';
import { parseSnippet, snippetKeys, type Snippet, type SnippetListParams } from './entity';
import {
  parseSnippetDetailResponse,
  parseSnippetsListResponse,
  parseSnippetUsageResponse,
  populateSnippetDetail,
  populateSnippetsList,
  populateSnippetUsage,
  type ParsedSnippetDetailResponse,
  type ParsedSnippetsListResponse,
  type ParsedSnippetUsageResponse,
} from './populate';

/**
 * Snippet read queries.
 *
 * Each query is exposed as the bulletproof-react triplet (plain async fn
 * + queryOptions factory + thin suspense hook). The detail and usage
 * endpoints update the snippet detail cache through `populate.ts`; the
 * list endpoint populates each row's detail slot so a subsequent
 * `useSnippet({ id })` hits the cache instead of re-fetching the bare row.
 */

const STALE_TIME = 60_000;

function ascQueryParam(asc: boolean | undefined): 'true' | 'false' | undefined {
  if (asc === undefined) return undefined;
  return asc ? 'true' : 'false';
}

/* ------------------------------------------------------------------------- */
/* getSnippets — paginated list                                               */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getSnippets(
  workspaceId: number,
  params: SnippetListParams,
  qc: QueryClient,
): Promise<ParsedSnippetsListResponse> {
  const raw = await hydraUiApi.snippets.list({
    params: {
      path: environmentPath(workspaceId),
      query: {
        page: params.page,
        limit: params.limit,
        search: params.search,
        sort: params.sort,
        asc: ascQueryParam(params.asc),
      },
    },
  });
  const response = parseSnippetsListResponse(raw);
  populateSnippetsList(qc, workspaceId, response);
  return response;
}

export const getSnippetsQueryOptions = (workspaceId: number, params: SnippetListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: snippetKeys.list(workspaceId, params),
    queryFn: () => getSnippets(workspaceId, params, qc),
    staleTime: STALE_TIME,
  });

interface UseSnippetsListOptions {
  params: SnippetListParams;
  queryConfig?: QueryConfig<typeof getSnippetsQueryOptions>;
}

/** @public */
export const useSnippetsList = ({ params, queryConfig }: UseSnippetsListOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getSnippetsQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getSnippet — by-id with usage sideload                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getSnippet(
  workspaceId: number,
  snippetId: string,
  qc: QueryClient,
): Promise<ParsedSnippetDetailResponse> {
  const raw = await hydraUiApi.snippets.get({
    params: { path: snippetPath(workspaceId, snippetId) },
  });
  const parsed = parseSnippetDetailResponse(raw);
  populateSnippetDetail(qc, workspaceId, parsed);
  return parsed;
}

/** @public */
export const getSnippetQueryOptions = (workspaceId: number, snippetId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: snippetKeys.detailWithUsage(workspaceId, snippetId),
    queryFn: () => getSnippet(workspaceId, snippetId, qc),
    staleTime: STALE_TIME,
  });

interface UseSnippetOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getSnippetQueryOptions>;
}

/** @public */
export const useSnippet = ({ id, queryConfig }: UseSnippetOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getSnippetQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

interface UseSnippetOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled detail hook. For callers that resolve a
 * snippet by id outside a suspense boundary and may keep the query dormant
 * (e.g. a resource resolver that only fetches when the resource is a snippet).
 * Mirrors `useDomainsOptional`: `skipToken` (not `enabled: false`) so no cache
 * slot is registered while inactive. `retry: false` so a 404 surfaces as
 * `isError` immediately for deleted-resource detection.
 *
 * @public
 */
export const useSnippetOptional = ({ workspaceId, id, enabled = true }: UseSnippetOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? snippetKeys.detailWithUsage(workspaceId, id) : ['snippet', 'inactive'],
    queryFn: active ? () => getSnippet(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/* ------------------------------------------------------------------------- */
/* getSnippetUsage — continuation-paginated usage details                     */
/* ------------------------------------------------------------------------- */

export interface GetSnippetUsageInput {
  workspaceId: number;
  snippetId: string;
  continuation: number;
}

/** @public */
export async function getSnippetUsage(
  input: GetSnippetUsageInput,
  qc: QueryClient,
): Promise<ParsedSnippetUsageResponse> {
  const raw = await hydraUiApi.snippets.getUsage({
    params: {
      path: {
        ...snippetPath(input.workspaceId, input.snippetId),
        continuation: String(input.continuation),
      },
    },
  });
  const parsed = parseSnippetUsageResponse(raw);
  populateSnippetUsage(qc, input.workspaceId, input.snippetId, parsed);
  return parsed;
}

export const getSnippetUsageQueryOptions = (input: GetSnippetUsageInput, qc: QueryClient) =>
  queryOptions({
    queryKey: [...snippetKeys.detail(input.workspaceId, input.snippetId), 'usage', input.continuation] as const,
    queryFn: () => getSnippetUsage(input, qc),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* getObjectTypeSnippets — filtered by object type                            */
/* ------------------------------------------------------------------------- */

export interface GetObjectTypeSnippetsInput {
  workspaceId: number;
  objectTypeIds: number[];
}

/** @public */
export async function getObjectTypeSnippets(
  input: GetObjectTypeSnippetsInput,
  qc: QueryClient,
): Promise<readonly Snippet[]> {
  const raw = await hydraUiApi.snippets.getObjectTypeSnippets({
    params: {
      path: environmentPath(input.workspaceId),
      query: { 'objectTypeIds[]': input.objectTypeIds },
    },
  });
  const parsed = raw.snippets.map(parseSnippet);
  for (const s of parsed) qc.setQueryData(snippetKeys.detail(input.workspaceId, s.id), s);
  return parsed;
}

export const getObjectTypeSnippetsQueryOptions = (input: GetObjectTypeSnippetsInput, qc: QueryClient) =>
  queryOptions({
    // Nest under `snippetKeys.lists(...)` so snippet mutations'
    // prefix-based `invalidateQueries({ queryKey: snippetKeys.lists(...) })`
    // also clears this filtered cache.
    queryKey: [
      ...snippetKeys.lists(input.workspaceId),
      'by-object-type',
      [...input.objectTypeIds].sort((a, b) => a - b),
    ] as const,
    queryFn: () => getObjectTypeSnippets(input, qc),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* useSnippets — store-wide read of every cached snippet                      */
/* ------------------------------------------------------------------------- */

/** @public */
export function useSnippets<TSelected = readonly Snippet[]>(
  options?: UseAllCachedOptions<Snippet, TSelected>,
): TSelected {
  const workspaceId = requireWorkspaceId();
  return useAllCached<Snippet, TSelected>(snippetKeys.details(workspaceId), options);
}
